import React, { memo, useMemo, useRef } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import type { ProductListItemModel } from 'autogen/swagger/all';
import ProductFavoriteButton from 'components/generic/productFavoriteButton';
import ProductImage from 'components/generic/productImage';
import ProductName from 'components/generic/productName';
import PriceModule from 'components/generic/productPrice/PriceModule';
import RatingStars from 'components/generic/ratingStars';
import type { IProductActionButton } from 'components/generic/productCTA/shared/types';
import useThemeHandler from 'components/shared/productItem/themes/useThemeHandler';
import ProductActionRenderer from 'components/generic/productCTA/ProductActionRenderer';
import useIsAuthenticated from 'hooks/user/useIsAuthenticated';
import { firstCharacterToLower } from 'modules/helpers/strings';
import { compactActionComponents } from 'components/generic/productCTA/shared/productActionComponents';
import { ProductCard, ProductCardBottom, ProductCardMid, ProductCardTop, ProductContent } from './shared/styles';
import ProductItemDebugger from './components/productDebug/ProductItemDebugger';
import serverSideTracking, { createProductItemTrackingContext } from './shared/helpers';
import type { ThemeType } from '../productList/shared/types';
import SplashOverlay from './components/VerticalProductItem/components/SplashOverlay';
import { TrackingEvent } from './shared/types';
import VariantRenderer from './components/Variant/VariantRenderer';
import useAdformImpressionTracking from 'hooks/tracking/useAdformImpressionTracking';
import LinkAction from '../Routing/components/LinkAction';
import SponsoredSplash from '../Splash/components/SponsoredSplash/SponsoredSplash';
import useRequestContext from 'hooks/user/useRequestContext';

/* #region  styles */
const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing4};

    ${({ theme }) => (theme as any).Wrapper}
`;

const AnchorOverlay = styled(LinkAction)`
    position: absolute;
    width: 100%;
    height: 100%;

    && {
        z-index: 2;
    }

    &:focus {
        outline: none;
    }
`;

const StyledProductCard = styled(ProductCard)`
    position: relative;
    height: 100%;
    border-radius: 0;

    a,
    button,
    label {
        z-index: 3;
    }

    &:has(${AnchorOverlay}:focus-visible) {
        outline: ${({ theme }) => theme.Spacing4} auto ${({ theme }) => theme.ColorBorderFocusRing};
        outline-offset: ${({ theme }) => theme.Spacing4};
        border-radius: ${({ theme }) => theme.RadiusMedium};
    }

    & button:focus {
        outline-offset: -1px; // added to prevent outline from being cut off due to it overflowing
    }
`;

const StyledProductName = styled(ProductName)<{ $isChanel?: boolean }>`
    ${({ $isChanel }) =>
        $isChanel &&
        css`
            & > * {
                font-family: Arial, Helvetica, sans-serif;
                font-weight: bold;

                &:last-child {
                    font-weight: normal;
                }
            }
        `}

    ${({ theme }) => (theme as any).StyledProductName}
`;

const StyledPriceModule = styled(PriceModule)`
    justify-items: start;
    text-align: left;
    align-content: end;

    ${({ theme }) => (theme as any).StyledPriceModule}
`;

const StyledProductActionRenderer = styled(ProductActionRenderer)`
    align-self: end;
`;

const VariantAndFavorite = styled.div`
    display: grid;
    grid-template-columns: 1fr 40px;
    grid-template-areas: 'Variant Favorite';
    align-items: center;
    gap: ${({ theme }) => theme.Spacing4};
`;

const StyledProductFavoriteButton = styled(ProductFavoriteButton)`
    grid-area: Favorite;
`;

const StyledSponsoredSplash = styled(SponsoredSplash)`
    margin-top: -${({ theme }) => theme.Spacing20}; // height of SponsoredSplash, to align product image with other product items
`;

/* #endregion */

const ProductItem = React.forwardRef<HTMLDivElement, any>(
    (
        {
            model,
            recommendationPlacement,
            themeType,
            pageContext,
            ...restProps
        }: {
            model: ProductListItemModel;
            recommendationPlacement: string;
            themeType?: ThemeType;
            pageContext?: 'ProductList';
        },
        ref
    ) => {
        const {
            /* ----------------General Product Item properties-------------*/
            id: productId,
            externalProductId,
            linkAction,
            primaryImage,
            splashes,
            priceInfo: { minimumSalesQuantity },
            priceInfo,
            showPossibleAdditionalDeliveryCost,
            favoriteStatus: { favoriteStatus },
            standardName, // name of product
            listName, // Name, Brand Desc, AmountAndUnit
            /* ----------------For Review Module-------------*/
            averageRating,
            showReviews,
            reviewCount,
            /* ----------------For Product Action-------------*/
            action, // actionType for the CTA
            variants, // VariantsInfo
            isSubscribable,
            isSponsored,
            isSubscriptionContext,
            addToBasketType,
            requireLoginForCheckout,
            fromPowerstep, // Whether the CTA was clicked from a powerstep only relevant for add to basket button
            showPowerstepOnAddToBasket, // Whether the CTA should open powerstep or not, only relevent for add to basket button
            bundleInfo, // Used when adding a bundle to basket, adds the params to the body of the req ( also used for abit of tracking )
            /* -----------------Tracking related-----------*/
            dataLayerDto,
            dataLayerDto: { listName: dataLayerListName },
            uaTrackingCategory,
            uaTrackingLabel,
            trackModel,
            /* ----------------- Debug -----------*/
            explainedScore,
            useCustomChanelStyling
        } = model;
        const isAuthenticated = useIsAuthenticated();

        const { posRequest } = useRequestContext();

        const trackingRef = useRef(null);

        const trackingContext = createProductItemTrackingContext(
            firstCharacterToLower(uaTrackingCategory),
            uaTrackingLabel,
            trackModel,
            bundleInfo,
            recommendationPlacement
        );

        const productActionButtonModel: IProductActionButton['model'] = {
            action,
            trackingContext,
            productId,
            externalProductId,
            productNavigationLink: linkAction,
            isSubscriptionContext,
            isSubscribable,
            minimumSalesQuantity,
            bundleInfo,
            listName: dataLayerListName,
            fromPowerstep,
            showPowerstepOnAddToBasket,
            addToBasketType,
            requireLoginForCheckout,
            recommendationPlacement,
            variants,
            customerNotification: {
                image: model.primaryImage.url,
                isPreorder: false,
                productId,
                productName: model.listName
            }
        };

        useAdformImpressionTracking({
            ref: trackingRef,
            impTrackingUrls: trackModel?.adformProductTrackingModel?.impressionTrackingUrls
        });

        const { ButtonStyling, ...theme } = useThemeHandler(themeType) as any;
        const stringifiedTrackModel = useMemo(() => JSON.stringify(trackModel), [trackModel]);
        const stringifiedDataLayerModel = useMemo(() => JSON.stringify(dataLayerDto), [dataLayerDto]);

        const algonomyTracking = (trackingEvent: TrackingEvent) => {
            if (trackModel) {
                serverSideTracking(stringifiedTrackModel, trackingEvent);
            }
        };
        const showFavoriteButton = isAuthenticated || !posRequest?.isPosRequest;

        return (
            <ThemeProvider theme={theme}>
                <Wrapper {...restProps}>
                    {pageContext === 'ProductList' && isSponsored && <StyledSponsoredSplash productId={productId} />}

                    <StyledProductCard
                        size={{ xs: 'xs', sm: 'sm', xl: 'md' }}
                        ref={ref}
                        data-datalayer={stringifiedDataLayerModel}
                        onClick={() => algonomyTracking(TrackingEvent.ProductClick)}
                    >
                        <ProductItemDebugger explainedScore={explainedScore} />
                        <AnchorOverlay action={linkAction} aria-label={model.standardName} ref={trackingRef} />

                        <ProductContent>
                            {/* Top Content */}
                            <ProductCardTop $isChanel={useCustomChanelStyling}>
                                {/* ProductImage - Displays the product image  */}
                                <ProductImage
                                    image={{ ...primaryImage, alt: standardName }}
                                    aria-label={standardName}
                                    width={245}
                                    maxWidth="245px"
                                    transformType="SquareScale"
                                />
                                <SplashOverlay splashes={splashes} />
                            </ProductCardTop>

                            {/* Mid Content */}
                            <ProductCardMid>
                                <VariantAndFavorite>
                                    <VariantRenderer variants={variants} />

                                    {showFavoriteButton && (
                                        <StyledProductFavoriteButton
                                            variant="favoriteGhost"
                                            initialFavoriteState={favoriteStatus === 'IsFavorite'}
                                            iconSize="lg"
                                            productId={productId}
                                            onClick={() => algonomyTracking(TrackingEvent.AddToFavourites)}
                                            uaTrackingCategory={firstCharacterToLower(uaTrackingCategory)}
                                        />
                                    )}
                                </VariantAndFavorite>

                                <StyledProductName
                                    productName={listName}
                                    size="md"
                                    $isChanel={useCustomChanelStyling}
                                />

                                {showReviews && reviewCount > 0 && (
                                    <RatingStars
                                        rating={averageRating}
                                        reviewCount={reviewCount}
                                        showAvgRatingCount={false}
                                    />
                                )}
                            </ProductCardMid>

                            {/* Bottom Content */}
                            <ProductCardBottom>
                                {/* PriceModule - Displays everything related to price */}
                                <StyledPriceModule
                                    showPossibleAdditionalDeliveryCost={showPossibleAdditionalDeliveryCost}
                                    size="sm"
                                    {...priceInfo}
                                />

                                {/* ProductAction - Displays the current product action button */}
                                <ThemeProvider theme={{ ButtonStyling } as any}>
                                    <StyledProductActionRenderer
                                        overriddenActionComponents={compactActionComponents}
                                        model={productActionButtonModel}
                                        css={css`
                                            flex: 1;
                                        `}
                                    />
                                </ThemeProvider>
                            </ProductCardBottom>
                        </ProductContent>
                    </StyledProductCard>
                </Wrapper>
            </ThemeProvider>
        );
    }
);

export default memo(ProductItem);
